import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Header } from '../components/Layout';
import { getOrder, updateOrder, submitRevenueForm, getRevenueByOrder, generateInvoiceFromOrder, createOrderDirect, getUsers } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PricingEditor } from '../components/PricingEditor';
import USARouteMap from '../components/USARouteMap';
import TransportFacts from '../components/TransportFacts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { ArrowLeft, Save, ExternalLink, DollarSign, CheckCircle, Map, CreditCard, FileText, Truck } from 'lucide-react';
import { toast } from 'sonner';

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'picked_up', label: 'Picked Up' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered', label: 'Delivered' },
];

const PAYMENT_METHODS = ['Zelle', 'COD', 'CashApp', 'Venmo', 'ACH', 'Card'];

const DEFAULT_PRICING = {
  standard: { deposit_fee: 150, carrier_fee: 60, total_price: 210 },
  expedited: { deposit_fee: 175, carrier_fee: 70, total_price: 245 },
  enclosed: { deposit_fee: 200, carrier_fee: 85, total_price: 285 },
};

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id || id === 'new';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [pricing, setPricing] = useState({ ...DEFAULT_PRICING });
  const [usersList, setUsersList] = useState([]);

  // Revenue form state
  const [showRevenueForm, setShowRevenueForm] = useState(false);
  const [existingRevenue, setExistingRevenue] = useState(null);
  const [revForm, setRevForm] = useState({
    customer_name: '', vehicle_info: '', route: '',
    deposit_amount: 0, total_price: 0, payment_method: 'Zelle', notes: '',
  });
  const [submittingRevenue, setSubmittingRevenue] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  useEffect(() => { getUsers().then(res => setUsersList(res.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (isNew) {
      setForm({ customer_name: '', phone: '', email: '', vehicle_year: '', vehicle_make: '', vehicle_model: '',
        pickup_city: '', pickup_state: '', pickup_zip: '', delivery_city: '', delivery_state: '', delivery_zip: '',
        pickup_address: '', delivery_address: '', shipping_type: 'standard', price: 0, deposit_fee: 150, carrier_fee: 0,
        agent_name: user?.full_name || '', assigned_to_id: user?.id || '', pickup_date: '', delivery_date: '', dispatch_notes: '', status: 'pending' });
      return;
    }
    const fetch = async () => {
      try {
        const res = await getOrder(id);
        setOrder(res.data);
        setForm(res.data);
        setPricing({
          standard: res.data.pricing_standard || DEFAULT_PRICING.standard,
          expedited: res.data.pricing_expedited || DEFAULT_PRICING.expedited,
          enclosed: res.data.pricing_enclosed || DEFAULT_PRICING.enclosed,
        });
        // Check if revenue form exists
        const revRes = await getRevenueByOrder(id);
        if (revRes.data) setExistingRevenue(revRes.data);
      } catch { toast.error('Failed to load order'); navigate('/orders'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id, navigate]);

  const fetchNotes = useCallback(async () => {
    try { const res = await api.get(`/orders/${id}/notes`); setNotes(res.data); } catch {}
  }, [id]);
  useEffect(() => { if (id) fetchNotes(); }, [id, fetchNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await api.post(`/orders/${id}/notes`, { text: newNote.trim() });
      setNotes(prev => [...prev, res.data]);
      setNewNote('');
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  const openRevenueForm = () => {
    const o = order || {};
    const vehicle = [o.vehicle_year, o.vehicle_make, o.vehicle_model].filter(Boolean).join(' ');
    const route = `${o.pickup_city || ''}, ${o.pickup_state || ''} → ${o.delivery_city || ''}, ${o.delivery_state || ''}`;
    setRevForm({
      customer_name: o.customer_name || '',
      vehicle_info: vehicle,
      route: route,
      deposit_amount: o.deposit_fee || o.price || 0,
      total_price: o.price || 0,
      payment_method: o.payment_method || 'Zelle',
      notes: '',
    });
    setShowRevenueForm(true);
  };

  const handleSubmitRevenue = async () => {
    if (revForm.deposit_amount <= 0) {
      toast.error('Deposit amount is required');
      return;
    }
    setSubmittingRevenue(true);
    try {
      const res = await submitRevenueForm({ order_id: id, ...revForm });
      setExistingRevenue(res.data);
      setShowRevenueForm(false);
      toast.success('Revenue submitted! Dashboard updated.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally { setSubmittingRevenue(false); }
  };

  const handlePricingChange = (updated) => {
    setPricing(updated);
    if (updated.standard) {
      setForm(f => ({ ...f, price: updated.standard.total_price, deposit_fee: updated.standard.deposit_fee, carrier_fee: updated.standard.carrier_fee }));
    }
  };

  const handleGenerateInvoice = async (type) => {
    setGeneratingInvoice(true);
    try {
      const res = await generateInvoiceFromOrder(id, type);
      toast.success(`${type === 'customer' ? 'Customer' : 'Carrier'} invoice generated!`);
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally { setGeneratingInvoice(false); }
  };

  const handleSave = async () => {
    if (isNew) {
      // Direct order creation
      if (!form.customer_name?.trim()) {
        toast.error('Customer name is required');
        return;
      }
      setSaving(true);
      try {
        const res = await createOrderDirect(form);
        toast.success(`Order ${res.data.order_number} created!`);
        navigate(`/orders/${res.data.id}`);
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create order'); }
      finally { setSaving(false); }
      return;
    }
    setShowSaveConfirm(true);
  };

  const doSave = async () => {
    setShowSaveConfirm(false);
    setSaving(true);
    try {
      const saveData = {
        ...form,
        pricing_standard: pricing.standard, pricing_expedited: pricing.expedited,
        pricing_enclosed: pricing.enclosed,
      };
      delete saveData._id; delete saveData.id; delete saveData.created_at;
      delete saveData.notes_list;
      const res = await updateOrder(id, saveData);
      setOrder(res.data);
      setForm(res.data);
      toast.success('Order updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const vehicleSearch = `${form.vehicle_year} ${form.vehicle_make} ${form.vehicle_model}`.trim();

  if (loading) return <Layout><div className="flex items-center justify-center h-[80vh]"><div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full" /></div></Layout>;

  return (
    <Layout>
      <Header title={isNew ? 'New Order' : `Order ${order?.order_number || ''}`}>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate('/orders')}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
          {!isNew && (
            <>
              <Button variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => handleGenerateInvoice('customer')} disabled={generatingInvoice} data-testid="gen-customer-invoice-btn">
                <FileText className="w-4 h-4 mr-2" />Customer Invoice
              </Button>
              <Button variant="outline" className="text-slate-600 border-slate-300 hover:bg-slate-50" onClick={() => handleGenerateInvoice('carrier')} disabled={generatingInvoice} data-testid="gen-carrier-invoice-btn">
                <Truck className="w-4 h-4 mr-2" />Carrier Invoice
              </Button>
              {!existingRevenue ? (
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openRevenueForm} data-testid="fill-revenue-btn">
                  <DollarSign className="w-4 h-4 mr-2" />Fill Revenue
                </Button>
              ) : (
                <Button variant="outline" className="text-emerald-600 border-emerald-200" disabled>
                  <CheckCircle className="w-4 h-4 mr-2" />Revenue Submitted
                </Button>
              )}
            </>
          )}
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-order-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : isNew ? 'Create Order' : 'Save'}
          </Button>
        </div>
      </Header>
      <div className="p-6 max-w-5xl" data-testid="order-detail-page">
        <div className="space-y-6">
          {/* Revenue Submitted Banner */}
          {existingRevenue && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between" data-testid="revenue-banner">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-semibold text-emerald-800">Revenue Submitted</p>
                  <p className="text-xs text-emerald-600">Deposit: ${existingRevenue.deposit_amount?.toLocaleString()} via {existingRevenue.payment_method} — by {existingRevenue.submitted_by}</p>
                </div>
              </div>
            </div>
          )}

          {/* Order & Quote Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Order Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div className="bg-slate-50 rounded-lg p-3"><span className="text-slate-500 block text-xs">Order #</span><span className="font-mono font-bold text-emerald-600">{order?.order_number}</span></div>
              <div className="bg-slate-50 rounded-lg p-3"><span className="text-slate-500 block text-xs">Quote #</span><span className="font-mono font-bold text-blue-600">{order?.quote_number}</span></div>
              <div className="bg-slate-50 rounded-lg p-3"><span className="text-slate-500 block text-xs">Agent</span><span className="font-medium">{order?.agent_name || '-'}</span></div>
              <div className="bg-slate-50 rounded-lg p-3"><span className="text-slate-500 block text-xs">Primary Price</span><span className="font-bold text-emerald-600">${(form.price || 0).toLocaleString()}</span></div>
              <div className="bg-slate-50 rounded-lg p-3" data-testid="order-distance-display"><span className="text-slate-500 block text-xs">Distance</span><span className="font-semibold text-indigo-600">{(form.estimated_distance || order?.estimated_distance) ? `${(form.estimated_distance || order?.estimated_distance).toLocaleString()} mi` : '—'}</span></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-100">
              <div>
                <Label className="text-xs text-slate-500">Shipment Type</Label>
                <select value={form.shipping_type || 'standard'} onChange={e => setForm(f => ({...f, shipping_type: e.target.value}))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-shipment-type">
                  <option value="standard">Standard Shipment</option>
                  <option value="expedited">Expedited Shipment</option>
                  <option value="enclosed">Enclosed Shipment</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Order Status</Label>
                <select value={form.status || 'pending'} onChange={e => setForm(f => ({...f, status: e.target.value}))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-order-status">
                  <option value="pending">Pending</option>
                  <option value="assigned">Assigned</option>
                  <option value="picked_up">Picked Up</option>
                  <option value="in_transit">In Transit</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>
            </div>
          </div>

          {/* Vehicle Information */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Vehicle Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><Label className="text-xs text-slate-500">Year</Label><Input value={form.vehicle_year || ''} onChange={e => setForm(f => ({...f, vehicle_year: e.target.value}))} data-testid="input-vehicle-year" /></div>
              <div><Label className="text-xs text-slate-500">Make</Label><Input value={form.vehicle_make || ''} onChange={e => setForm(f => ({...f, vehicle_make: e.target.value}))} data-testid="input-vehicle-make" /></div>
              <div><Label className="text-xs text-slate-500">Model</Label><Input value={form.vehicle_model || ''} onChange={e => setForm(f => ({...f, vehicle_model: e.target.value}))} data-testid="input-vehicle-model" /></div>
              <div>
                <Label className="text-xs text-slate-500">Body Type</Label>
                <select value={form.vehicle_body_type || ''} onChange={e => setForm(f => ({...f, vehicle_body_type: e.target.value}))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-vehicle-body-type">
                  <option value="">Select...</option>
                  {[{g:'Sedan',o:['Small Sedan','Medium Sedan','Large Sedan']},{g:'SUV',o:['Small SUV','Medium SUV','Large SUV']},{g:'Truck',o:['Pickup Truck','Box Truck','Flatbed Truck']},{g:'Van',o:['Minivan','Cargo Van','Passenger Van']},{g:'Coupe/Sports',o:['Coupe','Convertible','Sports Car']},{g:'Wagon/Hatch',o:['Hatchback','Station Wagon']},{g:'Specialty',o:['Motorcycle','ATV/UTV','RV/Motorhome']},{g:'Boat',o:['Small Boat','Medium Boat','Large Boat']},{g:'Trailer',o:['Small Trailer','Large Trailer','Enclosed Trailer']},{g:'Bus',o:['Mini Bus','Full Size Bus']},{g:'Other',o:['Other']}].map(g => (
                    <optgroup key={g.g} label={g.g}>{g.o.map(o => <option key={o} value={o}>{o}</option>)}</optgroup>))}
                </select>
              </div>
              <div><Label className="text-xs text-slate-500">Color</Label><Input value={form.vehicle_color || ''} onChange={e => setForm(f => ({...f, vehicle_color: e.target.value}))} data-testid="input-vehicle-color" /></div>
              <div><Label className="text-xs text-slate-500">VIN</Label><Input value={form.vehicle_vin || ''} onChange={e => setForm(f => ({...f, vehicle_vin: e.target.value}))} data-testid="input-vehicle-vin" /></div>
              <div><Label className="text-xs text-slate-500">License Plate</Label><Input value={form.vehicle_license || ''} onChange={e => setForm(f => ({...f, vehicle_license: e.target.value}))} /></div>
              <div>
                <Label className="text-xs text-slate-500">Running Status</Label>
                <select value={form.running_status || 'Running'} onChange={e => setForm(f => ({...f, running_status: e.target.value}))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-running-status">
                  <option value="Running">Running</option>
                  <option value="Not Running">Not Running</option>
                  <option value="Rolls/Steers/Brakes">Rolls/Steers/Brakes</option>
                  <option value="Inop - No Keys">Inop - No Keys</option>
                  <option value="Inop - Forklift">Inop - Forklift Required</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-6 mt-3 pt-3 border-t border-slate-100">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.modifications_lifted} onChange={e => setForm(f => ({...f, modifications_lifted: e.target.checked}))} className="rounded" />Lifted</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.modifications_lowered} onChange={e => setForm(f => ({...f, modifications_lowered: e.target.checked}))} className="rounded" />Lowered</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.modifications_oversized_tires} onChange={e => setForm(f => ({...f, modifications_oversized_tires: e.target.checked}))} className="rounded" />Oversized Tires</label>
            </div>
          </div>

          {/* Customer & Contact */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Customer & Contact</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><Label className="text-xs text-slate-500">Customer Name</Label><Input value={form.customer_name || ''} onChange={e => setForm(f => ({...f, customer_name: e.target.value}))} data-testid="input-customer-name" /></div>
              <div><Label className="text-xs text-slate-500">Phone</Label><Input value={form.phone || ''} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="input-phone" /></div>
              <div><Label className="text-xs text-slate-500">Email</Label><Input value={form.email || ''} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="input-email" /></div>
              <div><Label className="text-xs text-slate-500">Agent</Label><Input value={form.agent_name || ''} onChange={e => setForm(f => ({...f, agent_name: e.target.value}))} data-testid="input-agent-name" /></div>
              <div><Label className="text-xs text-slate-500">Source</Label><Input value={form.source || ''} onChange={e => setForm(f => ({...f, source: e.target.value}))} data-testid="input-source" /></div>
            </div>
          </div>

          {/* Pickup Information */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Pickup Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><Label className="text-xs text-slate-500">Person's Name</Label><Input value={form.pickup_person_name || ''} onChange={e => setForm(f => ({...f, pickup_person_name: e.target.value}))} data-testid="input-pickup-person-name" /></div>
              <div><Label className="text-xs text-slate-500">Company</Label><Input value={form.pickup_company || ''} onChange={e => setForm(f => ({...f, pickup_company: e.target.value}))} /></div>
              <div><Label className="text-xs text-slate-500">Address 1</Label><Input value={form.pickup_address1 || ''} onChange={e => setForm(f => ({...f, pickup_address1: e.target.value}))} /></div>
              <div><Label className="text-xs text-slate-500">Address 2</Label><Input value={form.pickup_address2 || ''} onChange={e => setForm(f => ({...f, pickup_address2: e.target.value}))} /></div>
              <div><Label className="text-xs text-slate-500">City</Label><Input value={form.pickup_city || ''} onChange={e => setForm(f => ({...f, pickup_city: e.target.value}))} data-testid="input-pickup-city" /></div>
              <div><Label className="text-xs text-slate-500">State</Label><Input value={form.pickup_state || ''} onChange={e => setForm(f => ({...f, pickup_state: e.target.value}))} data-testid="input-pickup-state" /></div>
              <div><Label className="text-xs text-slate-500">ZIP</Label><Input value={form.pickup_zip || ''} onChange={e => setForm(f => ({...f, pickup_zip: e.target.value}))} data-testid="input-pickup-zip" /></div>
              <div><Label className="text-xs text-slate-500">Email</Label><Input value={form.pickup_email || ''} onChange={e => setForm(f => ({...f, pickup_email: e.target.value}))} /></div>
              <div><Label className="text-xs text-slate-500">Phone</Label><Input value={form.pickup_phone || ''} onChange={e => setForm(f => ({...f, pickup_phone: e.target.value}))} /></div>
            </div>
          </div>

          {/* Delivery Information */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Delivery Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><Label className="text-xs text-slate-500">Person's Name</Label><Input value={form.delivery_person_name || ''} onChange={e => setForm(f => ({...f, delivery_person_name: e.target.value}))} data-testid="input-delivery-person-name" /></div>
              <div><Label className="text-xs text-slate-500">Company</Label><Input value={form.delivery_company || ''} onChange={e => setForm(f => ({...f, delivery_company: e.target.value}))} /></div>
              <div><Label className="text-xs text-slate-500">Address 1</Label><Input value={form.delivery_address1 || ''} onChange={e => setForm(f => ({...f, delivery_address1: e.target.value}))} /></div>
              <div><Label className="text-xs text-slate-500">Address 2</Label><Input value={form.delivery_address2 || ''} onChange={e => setForm(f => ({...f, delivery_address2: e.target.value}))} /></div>
              <div><Label className="text-xs text-slate-500">City</Label><Input value={form.delivery_city || ''} onChange={e => setForm(f => ({...f, delivery_city: e.target.value}))} data-testid="input-delivery-city" /></div>
              <div><Label className="text-xs text-slate-500">State</Label><Input value={form.delivery_state || ''} onChange={e => setForm(f => ({...f, delivery_state: e.target.value}))} data-testid="input-delivery-state" /></div>
              <div><Label className="text-xs text-slate-500">ZIP</Label><Input value={form.delivery_zip || ''} onChange={e => setForm(f => ({...f, delivery_zip: e.target.value}))} data-testid="input-delivery-zip" /></div>
              <div><Label className="text-xs text-slate-500">Email</Label><Input value={form.delivery_email || ''} onChange={e => setForm(f => ({...f, delivery_email: e.target.value}))} /></div>
              <div><Label className="text-xs text-slate-500">Phone</Label><Input value={form.delivery_phone || ''} onChange={e => setForm(f => ({...f, delivery_phone: e.target.value}))} /></div>
            </div>
          </div>

          {/* USA Route Map */}
          {(order?.pickup_state || order?.delivery_state) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="order-route-map">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Map className="w-5 h-5 text-slate-600" /></div>
                <h3 className="font-semibold text-slate-900">Transport Route</h3>
              </div>
              <USARouteMap pickupState={order?.pickup_state} deliveryState={order?.delivery_state} pickupCity={order?.pickup_city} deliveryCity={order?.delivery_city} />
            </div>
          )}

          {/* Order Pricing */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Order Pricing</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Reservation Fee (Received)</Label>
                <Input type="number" step="0.01" value={form.reservation_fee || ''} onChange={e => {
                  const v = parseFloat(e.target.value) || 0;
                  setForm(f => ({...f, reservation_fee: v, total_tariff: v + (f.carrier_fee || 0)}));
                }} className="font-bold text-emerald-600" data-testid="input-reservation-fee" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Carrier Fee</Label>
                <Input type="number" step="0.01" value={form.carrier_fee || ''} onChange={e => {
                  const v = parseFloat(e.target.value) || 0;
                  setForm(f => ({...f, carrier_fee: v, total_tariff: (f.reservation_fee || 0) + v}));
                }} className="font-bold text-blue-600" data-testid="input-carrier-fee" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Total Tariff</Label>
                <Input type="number" step="0.01" value={form.total_tariff || ''} onChange={e => setForm(f => ({...f, total_tariff: parseFloat(e.target.value) || 0}))}
                  className="font-bold text-slate-900" data-testid="input-total-tariff" />
                <p className="text-[10px] text-slate-400 mt-0.5">Reservation + Carrier = ${((form.reservation_fee || 0) + (form.carrier_fee || 0)).toLocaleString()}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Full COD</Label>
                <Input type="number" step="0.01" value={form.full_cod || ''} onChange={e => setForm(f => ({...f, full_cod: parseFloat(e.target.value) || 0}))}
                  className="font-bold text-amber-600" data-testid="input-full-cod" />
              </div>
            </div>
          </div>

          {/* Pricing — All Shipping Types */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Pricing — All Shipping Types</h3>
            <PricingEditor pricing={pricing} distance={form.estimated_distance || order?.estimated_distance} onChange={handlePricingChange} />
          </div>

          {/* Dispatch & Carrier */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Dispatch & Carrier</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Order Status</Label>
                <Select value={form.status || 'pending'} onValueChange={v => setForm(f => ({...f, status: v}))}>
                  <SelectTrigger data-testid="select-order-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{statusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={form.payment_method || ''} onValueChange={v => setForm(f => ({...f, payment_method: v}))}>
                  <SelectTrigger data-testid="select-payment-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Carrier Name</Label><Input value={form.carrier_name || ''} onChange={e => setForm(f => ({...f, carrier_name: e.target.value}))} data-testid="input-carrier-name" /></div>
              <div><Label>Carrier MC #</Label><Input value={form.carrier_mc || ''} onChange={e => setForm(f => ({...f, carrier_mc: e.target.value}))} /></div>
              <div><Label>Carrier Phone</Label><Input value={form.carrier_phone || ''} onChange={e => setForm(f => ({...f, carrier_phone: e.target.value}))} /></div>
              <div><Label>Driver Name</Label><Input value={form.driver_name || ''} onChange={e => setForm(f => ({...f, driver_name: e.target.value}))} data-testid="input-driver-name" /></div>
              <div><Label>Driver Phone</Label><Input value={form.driver_phone || ''} onChange={e => setForm(f => ({...f, driver_phone: e.target.value}))} /></div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Pickup & Delivery Dates</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Pickup Date</Label><Input type="date" value={form.pickup_date || ''} onChange={e => setForm(f => ({...f, pickup_date: e.target.value}))} /></div>
              <div><Label>Delivery Date</Label><Input type="date" value={form.delivery_date || ''} onChange={e => setForm(f => ({...f, delivery_date: e.target.value}))} /></div>
            </div>
          </div>

          {/* Transport Facts */}
          {(order?.pickup_state || order?.delivery_state) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="order-transport-facts">
              <h3 className="font-semibold text-slate-900 mb-4">Route Intelligence</h3>
              <TransportFacts pickupState={order?.pickup_state} deliveryState={order?.delivery_state} pickupCity={order?.pickup_city} deliveryCity={order?.delivery_city} />
            </div>
          )}

          {/* Notes */}
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="order-notes-section">
            <h3 className="font-semibold text-slate-900 mb-4">Notes</h3>
            <div className="flex gap-2 mb-4">
              <textarea className="flex-1 rounded-lg border border-slate-200 p-3 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a new note..." data-testid="new-note-input" />
              <Button className="bg-blue-600 hover:bg-blue-700 text-white self-end" onClick={handleAddNote} disabled={!newNote.trim()} data-testid="add-note-btn">Add Note</Button>
            </div>
            {notes.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {[...notes].reverse().map(note => (
                  <div key={note.id} className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-testid={`note-${note.id}`}>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{note.text}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                      <span className="font-medium text-slate-500">{note.user_name}</span>
                      <span>&bull;</span>
                      <span>{new Date(note.created_at).toLocaleDateString()}</span>
                      <span>{new Date(note.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 italic">No notes yet</p>}
          </div>

          {/* Dispatch Notes (Legacy) */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Dispatch Notes</h3>
            <textarea className="w-full rounded-lg border border-slate-200 p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.dispatch_notes || ''} onChange={e => setForm(f => ({...f, dispatch_notes: e.target.value}))} placeholder="Add dispatch notes..." />
          </div>
        </div>
      </div>

      {/* Revenue Form Dialog */}
      <Dialog open={showRevenueForm} onOpenChange={setShowRevenueForm}>
        <DialogContent className="max-w-lg" data-testid="revenue-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />Fill Revenue Form
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Customer</Label>
                <Input value={revForm.customer_name} onChange={e => setRevForm(f => ({...f, customer_name: e.target.value}))} data-testid="rev-customer" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Vehicle</Label>
                <Input value={revForm.vehicle_info} onChange={e => setRevForm(f => ({...f, vehicle_info: e.target.value}))} data-testid="rev-vehicle" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Route</Label>
              <Input value={revForm.route} onChange={e => setRevForm(f => ({...f, route: e.target.value}))} data-testid="rev-route" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Deposit Amount ($) *</Label>
                <Input type="number" step="0.01" value={revForm.deposit_amount}
                  onChange={e => setRevForm(f => ({...f, deposit_amount: parseFloat(e.target.value) || 0}))}
                  className="text-lg font-bold text-emerald-600" data-testid="rev-deposit" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Total Price ($)</Label>
                <Input type="number" step="0.01" value={revForm.total_price}
                  onChange={e => setRevForm(f => ({...f, total_price: parseFloat(e.target.value) || 0}))} data-testid="rev-total" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Payment Method *</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {PAYMENT_METHODS.map(m => (
                  <button key={m} onClick={() => setRevForm(f => ({...f, payment_method: m}))}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                      revForm.payment_method === m
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 hover:border-slate-300 text-slate-700'
                    }`} data-testid={`rev-method-${m.toLowerCase()}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Notes</Label>
              <Textarea value={revForm.notes} onChange={e => setRevForm(f => ({...f, notes: e.target.value}))} rows={2} placeholder="Any additional notes..." />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRevenueForm(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleSubmitRevenue} disabled={submittingRevenue} data-testid="submit-revenue-btn">
              <DollarSign className="w-4 h-4 mr-2" />{submittingRevenue ? 'Submitting...' : 'Submit Revenue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={showSaveConfirm}
        title="Save Changes"
        message="Save all changes to this order?"
        confirmText="Save"
        variant="success"
        onConfirm={doSave}
        onCancel={() => setShowSaveConfirm(false)}
      />
    </Layout>
  );
};

export default OrderDetail;